import {
  Button,
  Field,
  InfoLabel,
  Input,
  makeStyles,
  Spinner,
  Subtitle2,
} from "@fluentui/react-components";
import React, { useEffect, useRef, useState } from "react";
import { useAddInStore } from "../../../hooks";
import { createCADchatMeetingStageId, logger } from "../../../utils";
import { useOfficeItem } from "../../../providers";
import { SpaceAttendeeSelectList } from "../common";

const useStyles = makeStyles({
  title: {
    paddingLeft: "15px",
    paddingRight: "15px",
    marginBottom: "10px",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "15px",
  },
  section_centered: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    padding: "10px 15px",
  },
  member_list: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "10px 15px",
  },
  field: {
    width: "100%",
  },
  buttons: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "10px 15px",
    gap: "10px",
  },
  button: {
    width: "100%",
  },
});

export const CreateSpace = () => {
  const styles = useStyles();

  const { event: officeItem, officeService, isLoading } = useOfficeItem();

  // indicates the currently logged in user organization id
  const user = useAddInStore((state) => state.user);

  // used to set the meeting in the state
  const meeting = useAddInStore((state) => state.meeting);

  // indicates the selected organization id
  const selectedOrganizationId = useAddInStore((state) => state.selectedOrganizationId);

  // indicates the user created space name
  const [spaceName, setSpaceName] = useState<string | undefined>(undefined);

  // indicates that we are creating the space meeting
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);

  // prevents rapid repeated clicks before React commits disabled state
  const stageInProgressRef = useRef<boolean>(false);

  // indicates the current compose item already has a pending CADchat meeting marker
  const [isMeetingStaged, setIsMeetingStaged] = useState<boolean>(false);

  // indicates that we are removing a previously staged CADchat meeting
  const [removeInProgress, setRemoveInProgress] = useState<boolean>(false);

  // un-stages the pending CADchat meeting so the user can adjust or cancel it
  const handleRemovePendingMeeting = async () => {
    try {
      setRemoveInProgress(true);
      await officeService.removePendingCADchatMeetingDetails();
      setIsMeetingStaged(false);
    } catch (error) {
      logger.error("Error removing pending CADchat meeting: ", error);
    } finally {
      setRemoveInProgress(false);
    }
  };

  // used to update the selected space id
  const handleSpaceCreation = async () => {
    try {
      if (
        !selectedOrganizationId ||
        !officeItem?.start ||
        !officeItem?.end ||
        meeting ||
        isMeetingStaged ||
        stageInProgressRef.current
      ) {
        return;
      }

      // toggle save in progress
      stageInProgressRef.current = true;
      setSaveInProgress(true);

      // used to test for the default value outlook will set for some events
      const newEventRegex = /new event/i;

      // indicates the current event subject
      const currentSubject =
        officeItem?.subject &&
        officeItem?.subject !== "" &&
        !newEventRegex.test(officeItem?.subject)
          ? officeItem?.subject
          : (spaceName ?? "");

      // indicates the current space name and meeting name
      const meetingAndSpaceName =
        currentSubject !== "" ? currentSubject : `Workspace ${new Date().toLocaleDateString()}`;

      if (
        officeItem?.subject === "" ||
        newEventRegex.test(officeItem?.subject) ||
        !officeItem?.subject
      ) {
        await officeService.setSubject(meetingAndSpaceName);
      }

      await officeService.setLocation("CADchat Meeting");
      await officeService.addPendingCADchatMeetingDetails({
        stageId: createCADchatMeetingStageId(),
        mode: "new_space",
        organizationId: selectedOrganizationId,
        title: meetingAndSpaceName,
        spaceName: spaceName && spaceName !== "" ? spaceName : meetingAndSpaceName,
        organizerEmail: user?.login_email,
      });

      setIsMeetingStaged(true);
    } catch (error) {
      logger.error("Error creating space meeting: ", error);
    } finally {
      stageInProgressRef.current = false;

      // toggle save in progress
      setSaveInProgress(false);
    }
  };

  useEffect(() => {
    officeService
      .hasPendingCADchatMeetingDetails()
      .then(setIsMeetingStaged)
      .catch((error) => logger.error("Error checking pending CADchat meeting details:", error));
  }, [officeService, officeItem?.body]);

  return (
    <>
      <Subtitle2 className={styles.title}>Schedule in New Space</Subtitle2>
      <div className={styles.section_centered}>
        <Field
          className={styles.field}
          label={"Name Your Space"}
          id="space-name"
          hint={"If left empty this will be the same as the meeting name."}
        >
          <Input
            placeholder={
              officeItem?.subject && officeItem?.subject !== ""
                ? officeItem?.subject
                : `Workspace ${new Date().toLocaleDateString()}`
            }
            onChange={(e) => setSpaceName(e.target.value)}
          />
        </Field>
      </div>

      <div className={styles.member_list}>
        <SpaceAttendeeSelectList />
      </div>

      <div className={styles.section}>
        <InfoLabel
          size="medium"
          weight="semibold"
          info={`Once you click the "Add CADchat Meeting" button, this add-in will add CADchat meeting information to the calendar event. 
              You will still need to complete the creation of the CADchat meeting and calendar event from Outlook by clicking the "Send" button.`}
        >
          Send Invitations
        </InfoLabel>
      </div>

      <div className={styles.buttons}>
        <Button
          appearance="primary"
          className={styles.button}
          disabled={isLoading || saveInProgress || isMeetingStaged || !!meeting}
          icon={saveInProgress ? <Spinner size="tiny" /> : undefined}
          onClick={() => handleSpaceCreation()}
        >
          {isMeetingStaged ? "CADchat Meeting Added" : "Add CADchat Meeting"}
        </Button>
        {isMeetingStaged && !meeting ? (
          <Button
            appearance="secondary"
            className={styles.button}
            disabled={removeInProgress}
            icon={removeInProgress ? <Spinner size="tiny" /> : undefined}
            onClick={() => handleRemovePendingMeeting()}
          >
            Remove CADchat Meeting
          </Button>
        ) : null}
      </div>
    </>
  );
};
